import React from "react";
import { TrendingUp, TrendingDown } from "lucide-react";

// KPI tile — value with a coloured delta badge, matching the picture.
const Kpi = ({ label, value, unit, delta, up = true }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-2.5">
    <div className="truncate text-[9px] font-medium uppercase tracking-wide text-slate-400">{label}</div>
    <div className="mt-1 font-mono text-[15px] font-bold leading-none tabular-nums text-slate-800">
      {value}
      {unit && <span className="ml-1 text-[9px] font-medium text-slate-400">{unit}</span>}
    </div>
    <div className={`mt-1.5 inline-flex items-center gap-0.5 text-[8px] font-semibold ${up ? "text-[hsl(var(--lp-green-deep))]" : "text-[hsl(var(--lp-green-deep))]"}`}>
      {up ? <TrendingUp className="h-2.5 w-2.5" /> : <TrendingDown className="h-2.5 w-2.5" />}
      {delta} <span className="font-normal text-slate-400">vs ieri</span>
    </div>
  </div>
);

// Production line chart (kg) — points roughly mirror the picture's curve.
const LineChart = () => {
  const pts = [28, 18, 30, 22, 38, 26, 33, 20, 34, 24, 36, 21];
  const W = 240, H = 80;
  const stepX = W / (pts.length - 1);
  const max = 44;
  const coords = pts.map((v, i) => [i * stepX, H - (v / max) * (H - 8) - 4]);
  const line = coords.map(([x, y]) => `${x.toFixed(0)},${y.toFixed(0)}`).join(" ");
  const area = `0,${H} ${line} ${W},${H}`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="h-20 w-full" preserveAspectRatio="none">
      {[0.25, 0.5, 0.75].map((g) => (
        <line key={g} x1="0" x2={W} y1={H * g} y2={H * g} stroke="#EEF2F7" strokeWidth="1" />
      ))}
      <polygon points={area} fill="hsl(var(--lp-navy) / 0.08)" />
      <polyline points={line} fill="none" stroke="hsl(var(--lp-navy))" strokeWidth="2.5"
        strokeLinejoin="round" strokeLinecap="round" />
      {coords.map(([x, y]) => (
        <circle key={`pt-${x}-${y}`} cx={x} cy={y} r="2.4" fill="#fff" stroke="hsl(var(--lp-navy))" strokeWidth="1.6" />
      ))}
    </svg>
  );
};

const DONUT = [
  { label: "Telemea", value: 35, color: "hsl(214 84% 56%)" },
  { label: "Cașcaval", value: 27, color: "hsl(146 64% 42%)" },
  { label: "Brânză proaspătă", value: 18, color: "hsl(38 92% 55%)" },
  { label: "Iaurt", value: 12, color: "hsl(190 80% 45%)" },
  { label: "Altele", value: 8, color: "hsl(280 55% 62%)" },
];

const Donut = () => {
  let offset = 0;
  const r = 15.915, c = 100;
  return (
    <div className="flex items-center gap-3">
      <svg viewBox="0 0 42 42" className="h-[88px] w-[88px] shrink-0">
        <circle cx="21" cy="21" r={r} fill="none" stroke="#F1F5F9" strokeWidth="5.5" />
        {DONUT.map((s) => {
          const dash = `${s.value} ${c - s.value}`;
          const el = (
            <circle key={s.label} cx="21" cy="21" r={r} fill="none" stroke={s.color}
              strokeWidth="5.5" strokeDasharray={dash} strokeDashoffset={25 - offset} />
          );
          offset += s.value;
          return el;
        })}
      </svg>
      <ul className="space-y-1">
        {DONUT.map((s) => (
          <li key={s.label} className="flex items-center gap-1.5 text-[8px] text-slate-500">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
            {s.label}
          </li>
        ))}
      </ul>
    </div>
  );
};

// "Dashboard producție" card — the model shown in the reference picture.
export const ProductMock = () => (
  <div
    data-testid="landing-product-mock"
    className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-3.5 shadow-[0_28px_80px_hsl(var(--lp-ink)/0.22)] sm:p-4"
  >
    <div className="mb-3 flex items-center justify-between">
      <span className="text-[11px] font-bold text-slate-800">Dashboard producție</span>
      <span className="h-2 w-2 rounded-full bg-[hsl(var(--lp-green))]" />
    </div>

    <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
      <Kpi label="Lapte recepționat" value="25.480" unit="L" delta="+12%" />
      <Kpi label="Randament mediu" value="84,2%" delta="+3,4%" />
      <Kpi label="Producție totală" value="21.420" unit="kg" delta="+8%" />
      <Kpi label="Cost mediu / kg" value="7,48" unit="lei" delta="-2%" />
    </div>

    <div className="mt-2.5 grid grid-cols-1 gap-2 sm:grid-cols-5">
      <div className="rounded-lg border border-slate-200 bg-white p-2.5 sm:col-span-3">
        <div className="mb-1.5 text-[9px] font-semibold text-slate-600">Producție (kg)</div>
        <LineChart />
        <div className="mt-1 flex justify-between font-mono text-[7px] text-slate-400">
          <span>13 Mai</span><span>14 Mai</span><span>15 Mai</span>
          <span>16 Mai</span><span>17 Mai</span><span>18 Mai</span>
        </div>
      </div>
      <div className="rounded-lg border border-slate-200 bg-white p-2.5 sm:col-span-2">
        <div className="mb-1.5 text-[9px] font-semibold text-slate-600">Structură producție</div>
        <Donut />
      </div>
    </div>
  </div>
);
